import React, { useEffect, useRef, useState } from 'react';
import { Snackbar, Box, Typography, IconButton, Slide } from '@mui/material';
import { Close, CheckCircle } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { useOnboarding } from '../OnboardingProvider';

const CELEBRATION_SEEN_KEY = 'onboardingCelebrationSeen';

function SlideTransition(props: TransitionProps & { children: React.ReactElement }) {
  return <Slide {...props} direction="up" />;
}

/**
 * Fires a one-time celebratory toast when the user transitions from
 * incomplete to 100% in the current session. Persists a localStorage flag
 * so the toast doesn't re-appear across refreshes.
 */
export const CompletionCelebration: React.FC = () => {
  const { state } = useOnboarding();
  const [open, setOpen] = useState(false);
  const previousPercent = useRef<number | null>(null);

  useEffect(() => {
    if (!state) return;

    const current = state.progress.percent;
    const prev = previousPercent.current;
    previousPercent.current = current;

    if (prev === null) return;

    const justCrossed100 = prev < 100 && current >= 100;
    if (!justCrossed100) return;

    const seen = typeof window !== 'undefined' && localStorage.getItem(CELEBRATION_SEEN_KEY);
    if (seen) return;

    try {
      localStorage.setItem(CELEBRATION_SEEN_KEY, 'true');
    } catch {
      /* non-blocking */
    }
    setOpen(true);
  }, [state]);

  return (
    <Snackbar
      open={open}
      autoHideDuration={8000}
      onClose={() => setOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={SlideTransition}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: '#2F7D32',
          color: '#FFFFFF',
          borderRadius: 2,
          boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
          px: 2.5,
          py: 1.5,
          minWidth: 320,
          maxWidth: 480,
        }}
      >
        <CheckCircle sx={{ fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>Your clinic is all set up.</Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.9)', mt: 0.25 }}>
            Every step is done — StableTrack is ready for your next patient.
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="Close celebration"
          onClick={() => setOpen(false)}
          sx={{ color: 'rgba(255,255,255,0.9)' }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
    </Snackbar>
  );
};
